import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { isServerside } from '../../core/utils/isServerside';
import { FreyrData } from '../../types/Freyr';
import { getHawk } from '../../utils/global';
import type { BaseEventData, HawkEvent } from '../entry/sendEvent';

dayjs.extend(utc);

const toISOString = (date?: FreyrData['content']['article']['publishDate']): string | undefined => {
  if (!date?.year || !date.month || !date.day) {
    return undefined;
  }

  const parsedDate = dayjs.utc(`${date.year}-${date.month}-${date.day}`);
  return parsedDate.isValid() ? parsedDate.toISOString() : undefined;
};

const getScreenWidth = (): number =>
  window.document.documentElement.clientWidth ||
  window.document.body.clientWidth ||
  window.innerWidth;

export abstract class BaseEvent {
  public abstract hawkEvent: HawkEvent;

  private static readonly pageLoadEventPayload: Partial<FreyrData> =
    (isServerside()
      ? undefined
      : (window.freyr?.getAllValues?.().find((event) => event.name === 'pageLoadEvent')?.payload as
          | FreyrData
          | undefined)) ?? {};

  protected get fieWidgetsVersion(): BaseEventData['fieWidgetsVersion'] {
    return process.env.VERSION ?? 'unknown';
  }

  protected get site(): BaseEventData['context']['site'] {
    return getHawk().site;
  }

  protected get page(): BaseEventData['context']['page'] {
    const { page } = BaseEvent.pageLoadEventPayload;
    return {
      title: page?.title ?? document.title,
      url: page?.url ?? document.location.href,
      type: page?.type ?? undefined,
      category: page?.category ?? undefined,
      region: page?.region,
      tags: page?.tags ?? [],
      countryCode: page?.countryCode ?? undefined,
      sponsored: page?.sponsored,
    };
  }

  protected get content(): BaseEventData['context']['content'] {
    const { content } = BaseEvent.pageLoadEventPayload;
    return {
      article: {
        verticals: content?.article.verticals ?? [],
        subVerticals: content?.article.subVerticals ?? [],
        language: content?.article.language ?? undefined,
        authors: content?.article.authors ?? [],
        id: content?.article.id ?? undefined,
        publishDate: toISOString(content?.article.publishDate),
        updateDate: toISOString(content?.article.updateDate),
        cmsCategory: content?.article.cmsCategory ?? undefined,
        cmsChildCategory: content?.article.cmsChildcategory ?? undefined,
        cmsGrandchildCategory: content?.article.cmsGrandchildCategory ?? undefined,
      },
    };
  }

  protected get experiment(): BaseEventData['context']['experiment'] {
    const { experiment } = BaseEvent.pageLoadEventPayload;
    return {
      id: experiment?.id ?? undefined,
      name: experiment?.name ?? undefined,
      variantId: experiment?.variantId ?? undefined,
      variantName: experiment?.variantName ?? undefined,
      experimentSource: experiment?.experimentSource ?? undefined,
      battleId: window.VAN?.abTest?.battleId,
    };
  }

  protected get user(): BaseEventData['context']['user'] {
    return {
      loggedIn: !!window.FUTR?.Kiosq?.User,
      screenWidth: getScreenWidth(),
      userAgent: window.navigator.userAgent,
    };
  }
}
